import { useEffect, useState } from 'react';
import { SectionTitle, CommandPreview } from '../UI';

const MODELS = [
  { id: 'gpt-5.2-codex', label: 'gpt-5.2-codex', role: 'Agentic workflows', input: 1.75, output: 14.00, tier: 'flagship' },
  { id: 'gpt-5.2',       label: 'gpt-5.2',       role: 'Report synthesis',  input: 1.75, output: 14.00, tier: 'flagship' },
  { id: 'gpt-5-mini',    label: 'gpt-5-mini',    role: 'Balanced production', input: 0.25, output: 2.00, tier: 'balanced' },
  { id: 'gpt-4.1',       label: 'gpt-4.1',       role: 'Legacy stable',     input: 2.00, output: 8.00,  tier: 'legacy' },
  { id: 'gpt-4.1-mini',  label: 'gpt-4.1-mini',  role: 'Budget batching',   input: 0.40, output: 1.60,  tier: 'budget' },
  { id: 'gpt-4o',        label: 'gpt-4o',         role: 'Multimodal',        input: 2.50, output: 10.00, tier: 'legacy' },
  { id: 'gpt-4o-mini',   label: 'gpt-4o-mini',   role: 'Cheapest',          input: 0.15, output: 0.60,  tier: 'budget' },
];

const CMD_TOKENS = {
  audit:      { pages: 12, inputK: 80,  outputK: 12,  label: 'audit (standard, 12 pages)' },
  quick:      { pages: 3,  inputK: 15,  outputK: 4,   label: 'quick (3 pages)' },
  report:     { pages: 8,  inputK: 40,  outputK: 15,  label: 'report (markdown)' },
  'report-pdf':{ pages: 8, inputK: 45,  outputK: 18,  label: 'report-pdf' },
  brands:     { pages: 5,  inputK: 20,  outputK: 5,   label: 'brands scan' },
  citability: { pages: 5,  inputK: 25,  outputK: 6,   label: 'citability' },
  schema:     { pages: 4,  inputK: 18,  outputK: 8,   label: 'schema' },
};

const TIER_STYLE = {
  flagship: { bg: 'rgba(0,229,160,0.08)', color: 'var(--good)' },
  balanced: { bg: 'rgba(0,180,216,0.08)', color: 'var(--info)' },
  legacy:   { bg: 'rgba(255,209,102,0.08)', color: 'var(--moderate)' },
  budget:   { bg: 'rgba(255,255,255,0.04)', color: 'var(--text-3)' },
};

export default function ModelsTab({ url, defaultModel }) {
  const [selectedModel, setSelectedModel] = useState(defaultModel || 'gpt-5.2-codex');
  const [selectedCmd, setSelectedCmd] = useState('audit');

  useEffect(() => {
    if (defaultModel) {
      setSelectedModel(defaultModel);
    }
  }, [defaultModel]);

  const model = MODELS.find(m => m.id === selectedModel);
  const cmd = CMD_TOKENS[selectedCmd];

  const cost = model && cmd
    ? ((cmd.inputK / 1000) * model.input + (cmd.outputK / 1000) * model.output)
    : 0;

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '24px', fontWeight: '700', marginBottom: '6px', letterSpacing: '-0.5px' }}>
          AI Models
        </h1>
        <p style={{ fontSize: '14px', color: 'var(--text-3)' }}>
          OpenAI model strategy for GEO workflows. Choose the right model per task to balance quality and cost.
        </p>
      </div>

      <SectionTitle>Model catalog</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '10px', marginBottom: '28px' }}>
        {MODELS.map(m => {
          const ts = TIER_STYLE[m.tier];
          const isSelected = selectedModel === m.id;
          return (
            <div
              key={m.id}
              onClick={() => setSelectedModel(m.id)}
              style={{
                background: isSelected ? ts.bg : 'var(--bg-card)',
                border: isSelected ? `1px solid ${ts.color}` : '1px solid var(--border)',
                borderRadius: '12px',
                padding: '14px 16px',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = 'var(--border-med)'; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div style={{ fontSize: '13px', fontFamily: 'var(--font-mono)', color: isSelected ? ts.color : 'var(--text-1)', fontWeight: '500' }}>{m.label}</div>
                <span style={{ fontSize: '10px', padding: '2px 7px', borderRadius: '10px', background: ts.bg, color: ts.color, fontFamily: 'var(--font-mono)' }}>{m.tier}</span>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-3)', marginBottom: '10px' }}>{m.role}</div>
              <div style={{ display: 'flex', gap: '12px', fontSize: '12px', fontFamily: 'var(--font-mono)' }}>
                <span><span style={{ color: 'var(--text-3)' }}>in </span><span style={{ color: 'var(--text-2)' }}>${m.input}/M</span></span>
                <span><span style={{ color: 'var(--text-3)' }}>out </span><span style={{ color: 'var(--text-2)' }}>${m.output}/M</span></span>
              </div>
            </div>
          );
        })}
      </div>

      <SectionTitle>Cost estimator</SectionTitle>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <div style={{ flex: 1, minWidth: '160px' }}>
            <div style={{ fontSize: '11px', color: 'var(--text-3)', fontFamily: 'var(--font-mono)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Command</div>
            <select
              value={selectedCmd}
              onChange={e => setSelectedCmd(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-1)', fontSize: '13px', fontFamily: 'var(--font-mono)', outline: 'none' }}
            >
              {Object.entries(CMD_TOKENS).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: '160px' }}>
            <div style={{ fontSize: '11px', color: 'var(--text-3)', fontFamily: 'var(--font-mono)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Model</div>
            <select
              value={selectedModel}
              onChange={e => setSelectedModel(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-1)', fontSize: '13px', fontFamily: 'var(--font-mono)', outline: 'none' }}
            >
              {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
        </div>

        {model && cmd && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px', marginBottom: '16px' }}>
            {[
              { label: 'Est. input tokens',  value: `~${cmd.inputK}K` },
              { label: 'Est. output tokens', value: `~${cmd.outputK}K` },
              { label: 'Input cost',         value: `$${((cmd.inputK / 1000) * model.input).toFixed(3)}` },
              { label: 'Output cost',        value: `$${((cmd.outputK / 1000) * model.output).toFixed(3)}` },
            ].map(s => (
              <div key={s.label} style={{ background: 'var(--bg-surface)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '16px', fontWeight: '600', fontFamily: 'var(--font-mono)', color: 'var(--text-1)', marginBottom: '4px' }}>{s.value}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-3)' }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ background: 'var(--bg-surface)', borderRadius: '10px', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-2)' }}>Total estimated cost</div>
          <div style={{ fontSize: '28px', fontWeight: '700', fontFamily: 'var(--font-display)', color: 'var(--accent)' }}>
            ~${cost.toFixed(3)}
          </div>
        </div>
      </div>

      <SectionTitle>Run estimate script</SectionTitle>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px' }}>
        <CommandPreview cmd={`python3 scripts/estimate_openai_cost.py ${selectedCmd} --model ${selectedModel}`} url="" />
        <button
          onClick={() => navigator.clipboard?.writeText(`python3 scripts/estimate_openai_cost.py ${selectedCmd} --model ${selectedModel}`)}
          style={{ marginTop: '12px', padding: '10px 20px', background: 'var(--accent)', border: 'none', borderRadius: '8px', color: '#0a0a0f', fontSize: '13px', fontWeight: '600', fontFamily: 'var(--font-display)', cursor: 'pointer', width: '100%' }}
        >
          Copy estimate command →
        </button>
      </div>
    </div>
  );
}
